// The developer/agent documentation surface, rendered at /docs and /for-agents.
// Everything on this page reads from src/lib/agent-surface.ts so it can never
// drift from the real API, the MCP manifest or llms.txt.

import { SiteFooter } from "@/components/site-footer";
import { ENDPOINTS, MCP_TOOLS, MCP_URL, API_VERSION, WHEN_TO_USE } from "@/lib/agent-surface";
import { SITE } from "@/lib/site-content";

function Code({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-2xl border border-border bg-muted/40 p-5 text-[0.8rem] leading-relaxed">
      <code>{children}</code>
    </pre>
  );
}

export function AgentDocs({ headingId = "fa-h" }: { headingId?: string }) {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
        <section
          aria-labelledby={headingId}
          className="relative mx-auto max-w-[1400px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-16 md:pb-24"
        >
          <p className="text-sm uppercase tracking-[0.18em] text-muted-foreground">
            the Vibing Company · developer &amp; agent docs · v{API_VERSION}
          </p>
          <h1
            id={headingId}
            className="mt-6 font-display tracking-[-0.035em] leading-[1.02] max-w-[20ch]"
            style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
          >
            the Vibing Company API, MCP server &amp; docs.
          </h1>
          <p className="mt-10 max-w-[62ch] text-xl md:text-2xl leading-snug">
            Every page, every price and all {`2,000+`} curated resources in The Vibing Library are
            available as clean markdown, open JSON and a public MCP server. No key, no rate-limit
            form, no scraping required.
          </p>
        </section>

        <section
          aria-labelledby="fa-30s"
          className="border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-24"
        >
          <h2 id="fa-30s" className="font-display text-3xl md:text-4xl tracking-[-0.03em]">
            Start in 30 seconds
          </h2>
          <ul className="mt-8 max-w-[70ch] space-y-3 text-lg text-muted-foreground leading-relaxed">
            <li>No API key. No sign-up. No sales call. Every endpoint below is open right now.</li>
            <li>
              No registration for rate limits — stay under a few requests per second and you are
              fine.
            </li>
            <li>
              Sandbox: add <code className="font-mono text-sm">?sample=1</code> to the search
              endpoint for a fixed, shape-identical response you can wire against.
            </li>
          </ul>
          <div className="mt-10 grid lg:grid-cols-2 gap-8 items-start">
            <Code>{`# live
curl "${SITE.url}/api/public/library/search?q=shader&limit=3"

# sandbox (fixed response)
curl "${SITE.url}/api/public/library/search?sample=1"`}</Code>
            <Code>{`{
  "mcpServers": {
    "vibing": { "type": "http", "url": "${MCP_URL}" }
  }
}`}</Code>
          </div>
        </section>

        <section
          aria-labelledby="fa-when"
          className="border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-24"
        >
          <h2 id="fa-when" className="font-display text-3xl md:text-4xl tracking-[-0.03em]">
            When to use this site
          </h2>
          <ul className="mt-8 max-w-[76ch] space-y-4 text-lg text-muted-foreground leading-relaxed">
            {WHEN_TO_USE.use.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <h3 className="mt-12 font-display text-xl tracking-[-0.02em]">When not to</h3>
          <ul className="mt-4 max-w-[76ch] space-y-3 text-muted-foreground leading-relaxed">
            {WHEN_TO_USE.avoid.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </section>

        <section
          aria-labelledby="fa-start"
          className="border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-24"
        >
          <h2 id="fa-start" className="font-display text-3xl md:text-4xl tracking-[-0.03em]">
            Developer resources
          </h2>
          <ul className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              { t: "/llms.txt", b: "The index: who we are, what we sell, where the data lives.", href: "/llms.txt" },
              { t: "/llms-full.txt", b: "Everything in one file — services, guides, API and MCP reference.", href: "/llms-full.txt" },
              { t: "/api/public/openapi.json", b: "OpenAPI 3.1 description of every public endpoint.", href: "/api/public/openapi.json" },
              { t: "/.well-known/mcp.json", b: "MCP manifest: transport, endpoint, auth and the tool list.", href: "/.well-known/mcp.json" },
              { t: "/library/prompt.txt", b: "Copy-paste prompt that teaches any agent to use this site.", href: "/library/prompt.txt" },
              { t: "/sitemap.xml", b: "Every indexable page, including all library entries.", href: "/sitemap.xml" },
            ].map((c) => (
              <li key={c.t} className="rounded-3xl border border-border p-8">
                <a className="font-mono text-sm underline underline-offset-4" href={c.href}>
                  {c.t}
                </a>
                <p className="mt-4 text-muted-foreground leading-relaxed">{c.b}</p>
              </li>
            ))}
          </ul>
        </section>

        <section
          aria-labelledby="fa-mcp"
          className="border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-24"
        >
          <h2 id="fa-mcp" className="font-display text-3xl md:text-4xl tracking-[-0.03em]">
            Public MCP server
          </h2>
          <p className="mt-6 max-w-[62ch] text-lg text-muted-foreground leading-relaxed">
            Read-only, unauthenticated, streamable HTTP. Add it to Claude, ChatGPT or any MCP client
            and your agent can search the library and read this site instantly. Discovery manifest:{" "}
            <a className="font-mono text-sm underline underline-offset-4" href="/.well-known/mcp.json">
              /.well-known/mcp.json
            </a>
            .
          </p>
          <div className="mt-10 grid lg:grid-cols-2 gap-8 items-start">
            <Code>{`{
  "mcpServers": {
    "vibing": {
      "type": "http",
      "url": "${MCP_URL}"
    }
  }
}`}</Code>
            <ul className="space-y-4">
              {MCP_TOOLS.map((t) => (
                <li key={t.name} className="rounded-2xl border border-border p-5">
                  <span className="font-mono text-sm">{t.name}</span>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t.description}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section
          aria-labelledby="fa-api"
          className="border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-24"
        >
          <h2 id="fa-api" className="font-display text-3xl md:text-4xl tracking-[-0.03em]">
            JSON endpoints
          </h2>
          <p className="mt-6 max-w-[62ch] text-lg text-muted-foreground leading-relaxed">
            All CORS-open, all cached at the edge, all unauthenticated.
          </p>
          <ul className="mt-10 divide-y divide-border border-y border-border">
            {ENDPOINTS.map((e) => (
              <li key={e.path} className="py-6 grid md:grid-cols-[minmax(0,22rem)_1fr] gap-4">
                {e.method === "GET" ? (
                  <a className="font-mono text-sm underline underline-offset-4 break-all" href={e.path}>
                    GET {e.path}
                  </a>
                ) : (
                  <span className="font-mono text-sm break-all">
                    {e.method} {e.path}
                  </span>
                )}
                <div>
                  <p className="text-muted-foreground leading-relaxed">{e.description}</p>
                  {e.body && (
                    <dl className="mt-3 text-sm text-muted-foreground/90 space-y-1">
                      {Object.entries(e.body).map(([field, desc]) => (
                        <div key={field} className="flex gap-3">
                          <dt className="font-mono">{field}</dt>
                          <dd>{desc}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  {e.params && e.params.length > 0 && (
                    <dl className="mt-3 text-sm text-muted-foreground/90 space-y-1">
                      {e.params.map((p) => (
                        <div key={p.name} className="flex gap-3">
                          <dt className="font-mono">
                            {p.name}
                            {p.required ? "*" : ""}
                          </dt>
                          <dd>{p.description}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section
          aria-labelledby="fa-errors"
          className="border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-24"
        >
          <h2 id="fa-errors" className="font-display text-3xl md:text-4xl tracking-[-0.03em]">
            Errors
          </h2>
          <p className="mt-6 max-w-[62ch] text-lg text-muted-foreground leading-relaxed">
            Every failure is JSON with a stable <code className="font-mono text-sm">code</code>, a
            human message and a resolution hint — never an HTML page. Missing pages return HTTP 404
            with a markdown body pointing at the sitemap and this page.
          </p>
          <div className="mt-10">
            <Code>{`{
  "error": {
    "code": "gem_not_found",
    "message": "No gem exists with slug \\"nope\\".",
    "hint": "Search first: GET ${SITE.url}/api/public/library/search?q=nope",
    "docs": "${SITE.url}/docs",
    "status": 404
  }
}`}</Code>
          </div>
        </section>

        <section
          aria-labelledby="fa-md"
          className="border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-24"
        >
          <h2 id="fa-md" className="font-display text-3xl md:text-4xl tracking-[-0.03em]">
            Markdown twins
          </h2>
          <p className="mt-6 max-w-[62ch] text-lg text-muted-foreground leading-relaxed">
            Prefix any public path with <code className="font-mono">/md</code> to get that page as
            clean markdown with front matter — no nav, no scripts, no token waste.
          </p>
          <div className="mt-10">
            <Code>{`curl ${SITE.url}/md/guides/what-is-vibe-coding
curl ${SITE.url}/md/services/mvp-development
curl ${SITE.url}/md/library/<slug>`}</Code>
          </div>
        </section>

        <section
          aria-labelledby="fa-rules"
          className="border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-24"
        >
          <h2 id="fa-rules" className="font-display text-3xl md:text-4xl tracking-[-0.03em]">
            Ground rules
          </h2>
          <ul className="mt-8 max-w-[70ch] space-y-4 text-lg text-muted-foreground leading-relaxed">
            <li>
              Free for agents and humans. Be reasonable — cache responses and stay under a few
              requests per second.
            </li>
            <li>
              Attribution is appreciated: link back to the page you used, e.g.{" "}
              <span className="font-mono text-sm">https://vibing.inc/library/&lt;slug&gt;</span>.
            </li>
            <li>Library entries link to third-party projects; their own licences apply to their code.</li>
            <li>
              Something wrong or missing? Mail{" "}
              <a className="underline underline-offset-4" href={`mailto:${SITE.email}`}>
                {SITE.email}
              </a>
              .
            </li>
          </ul>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
